import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import matterportService from '../services/matterportService';
import { useLanguage } from '../context/LanguageContext';
import { Z_INDEX } from '../const';
import quickNavConfig from '../config/quickNavConfig.json';
import './LocationGallery.css';

/**
 * POI photo gallery (proximity-driven).
 * - Tiles appear on the right when the viewer is inside the active POI's
 *   `galleryRadius` (NavigationHints emits `poiGalleryActive` /
 *   `poiGalleryInactive` events as the user moves).
 * - Suppressed while a guided tour is running (uses its own overlays).
 * - Per-stop opt-out: set `showGalleryTiles: false` on the matching
 *   `quickNavConfig.locations[]` entry (matched by `targetPoiId === hintId`)
 *   to hide tiles for that POI even when in proximity. Default is `true`.
 * - Same behaviour for footer-button navigation and free walking — both
 *   bring the camera near the POI, which trips the proximity scan.
 * - Click tile → fullscreen lightbox with prev/next/close + keyboard nav.
 */
const LocationGallery = () => {
  const { language } = useLanguage();
  // Active POI gallery payload, shaped: { hintId, labels, gallery }.
  const [poiHint, setPoiHint] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  // Don't render at all while the guided tour is running — the tour has its
  // own overlays/controls and we want zero re-mount flicker between steps.
  const [tourRunning, setTourRunning] = useState(false);

  useEffect(() => {
    const onTourStep = () => { setTourRunning(true); setPoiHint(null); };
    const onTourStop = () => { setTourRunning(false); };
    const onPoiActive = (e) => setPoiHint(e.detail || null);
    const onPoiInactive = () => setPoiHint(null);

    matterportService.addEventListener('tourStep', onTourStep);
    matterportService.addEventListener('tourStop', onTourStop);
    window.addEventListener('poiGalleryActive', onPoiActive);
    window.addEventListener('poiGalleryInactive', onPoiInactive);
    return () => {
      matterportService.removeEventListener('tourStep', onTourStep);
      matterportService.removeEventListener('tourStop', onTourStop);
      window.removeEventListener('poiGalleryActive', onPoiActive);
      window.removeEventListener('poiGalleryInactive', onPoiInactive);
    };
  }, []);

  const gallery = (!tourRunning && poiHint && Array.isArray(poiHint.gallery))
    ? poiHint.gallery
    : [];
  const label = poiHint?.labels?.[language] || poiHint?.labels?.en || '';

  // Per-stop opt-out via quickNavConfig.locations[].showGalleryTiles.
  // Match by `targetPoiId === poiHint.hintId`. Default true if not set.
  const tilesEnabled = (() => {
    if (!poiHint?.hintId) return true;
    const loc = quickNavConfig.locations.find((l) => l.targetPoiId === poiHint.hintId);
    return loc?.showGalleryTiles !== false;
  })();

  // gallery sources may be data:/blob:/http(s) URLs (uploaded via the POI
  // editor) or static asset paths from public/. Prefix only the latter.
  const resolveSrc = useCallback((src) => (
    /^(data:|https?:|blob:)/i.test(src) ? src : process.env.PUBLIC_URL + src
  ), []);

  // Reset lightbox whenever the active POI changes
  useEffect(() => { setLightboxIdx(null); }, [poiHint?.hintId]);

  const open  = useCallback((i) => setLightboxIdx(i), []);
  const close = useCallback(() => setLightboxIdx(null), []);
  const next  = useCallback(() =>
    setLightboxIdx(i => (i + 1) % gallery.length), [gallery.length]);
  const prev  = useCallback(() =>
    setLightboxIdx(i => (i - 1 + gallery.length) % gallery.length), [gallery.length]);

  // Keyboard nav while lightbox is open
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape')     close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, close, next, prev]);

  const activeItem = lightboxIdx !== null ? gallery[lightboxIdx] : null;
  const hasGallery = gallery.length > 0 && !tourRunning && tilesEnabled;

  return (
    <>
      {/* Tile strip — renders only while a gallery is available for the stop */}
      {hasGallery && (
        <div
          className="loc-gallery"
          style={{ zIndex: Z_INDEX.MEDIA_TRAY }}
          key={poiHint.hintId}
          aria-label={label || 'Gallery'}
        >
          <div className="loc-gallery__label">
            {label}
          </div>
          <div className="loc-gallery__tiles">
            {gallery.map((item, i) => (
              <button
                key={`${poiHint.hintId}-${i}`}
                type="button"
                className="loc-gallery__tile"
                onClick={() => open(i)}
                aria-label={item.caption?.[language] || item.caption?.en || `Photo ${i + 1}`}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <img
                  src={resolveSrc(item.src)}
                  alt={item.caption?.[language] || item.caption?.en || ''}
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Windows-Photo-Viewer-style lightbox */}
      {activeItem && createPortal(
        <div
          className="loc-lightbox"
          style={{ zIndex: Z_INDEX.MEDIA_LIGHTBOX }}
          onClick={close}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="loc-lightbox__close"
            onClick={close}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>

          {gallery.length > 1 && (
            <button
              type="button"
              className="loc-lightbox__nav loc-lightbox__nav--prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
          )}

          <div
            className="loc-lightbox__stage"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={lightboxIdx}
              className="loc-lightbox__img"
              src={resolveSrc(activeItem.src)}
              alt={activeItem.caption?.[language] || activeItem.caption?.en || ''}
              decoding="async"
            />
            {(activeItem.caption?.[language] || activeItem.caption?.en) && (
              <div className="loc-lightbox__caption">
                <span className="loc-lightbox__caption-text">
                  {activeItem.caption?.[language] || activeItem.caption?.en}
                </span>
                {gallery.length > 1 && (
                  <span className="loc-lightbox__counter">
                    {lightboxIdx + 1} / {gallery.length}
                  </span>
                )}
              </div>
            )}
          </div>

          {gallery.length > 1 && (
            <button
              type="button"
              className="loc-lightbox__nav loc-lightbox__nav--next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default LocationGallery;
